import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Star,
  Save,
  Send,
  Sparkles,
  AlertTriangle,
  FileText,
  Clock,
  CheckCircle,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'

interface Competency {
  id: string
  name: string
  description: string
  weight: number
  field: string  // maps to backend field name
}

const competencies: Competency[] = [
  {
    id: 'communication',
    name: 'Communication',
    description: 'Clarity, articulation, active listening',
    weight: 20,
    field: 'communication_score',
  },
  {
    id: 'technical',
    name: 'Technical Skills',
    description: 'Domain expertise, skill proficiency',
    weight: 25,
    field: 'technical_score',
  },
  {
    id: 'problem_solving',
    name: 'Problem Solving',
    description: 'Analytical thinking, approach to challenges',
    weight: 20,
    field: 'problem_solving_score',
  },
  {
    id: 'leadership',
    name: 'Leadership',
    description: 'Initiative, team collaboration, decision-making',
    weight: 15,
    field: 'leadership_score',
  },
  {
    id: 'cultural_fit',
    name: 'Cultural Fit',
    description: 'Values alignment, team dynamics',
    weight: 20,
    field: 'cultural_fit_score',
  },
]

interface EvaluationData {
  id: number
  session_id: number | null
  booking_id: number | null
  status: string
  communication_score: number | null
  technical_score: number | null
  problem_solving_score: number | null
  leadership_score: number | null
  cultural_fit_score: number | null
  strengths: string[]
  improvements: string[]
  summary: string | null
  red_flags: string[]
  notes: string | null
  client_name: string | null
  session_title: string | null
  scheduled_at: string | null
}

export default function EvaluationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [evaluation, setEvaluation] = useState<EvaluationData | null>(null)
  // scores keyed by competency.id (e.g. 'communication', 'technical', ...)
  const [scores, setScores] = useState<Record<string, number>>({})
  const [strengths, setStrengths] = useState<string[]>([''])
  const [improvements, setImprovements] = useState<string[]>([''])
  const [summary, setSummary] = useState('')
  // red_flags is list[str] on backend — store as newline-separated textarea
  const [redFlags, setRedFlags] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generatingAi, setGeneratingAi] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!id) return
    const fetchEvaluation = async () => {
      try {
        const data = await evaluationsApi.getEvaluation(parseInt(id))
        setEvaluation(data)
        // Map individual backend score fields → local scores Record
        const loadedScores: Record<string, number> = {}
        for (const comp of competencies) {
          const val = data[comp.field as keyof typeof data] as number | null
          if (val != null) loadedScores[comp.id] = val
        }
        setScores(loadedScores)
        setStrengths(data.strengths?.length > 0 ? data.strengths : [''])
        setImprovements(data.improvements?.length > 0 ? data.improvements : [''])
        setSummary(data.summary ?? '')
        setRedFlags(data.red_flags?.join('\n') ?? '')
        setNotes(data.notes ?? '')
      } catch (err) {
        console.error('Failed to fetch evaluation:', err)
        setError('Failed to load evaluation.')
      } finally {
        setLoading(false)
      }
    }
    fetchEvaluation()
  }, [id])

  const handleScoreChange = (competencyId: string, score: number) => {
    setScores((prev) => ({ ...prev, [competencyId]: score }))
  }

  const calculateOverall = () => {
    let totalWeight = 0
    let weightedSum = 0
    competencies.forEach((comp) => {
      if (scores[comp.id]) {
        weightedSum += scores[comp.id] * comp.weight
        totalWeight += comp.weight
      }
    })
    return totalWeight > 0 ? (weightedSum / totalWeight).toFixed(1) : '--'
  }

  /** Build the individual score fields payload for UpdateEvaluationRequest */
  const buildScorePayload = () => {
    const payload: Record<string, number | null> = {}
    for (const comp of competencies) {
      payload[comp.field] = scores[comp.id] ?? null
    }
    return payload
  }

  const handleListChange = (
    list: string[],
    setList: (v: string[]) => void,
    index: number,
    value: string
  ) => {
    const updated = [...list]
    updated[index] = value
    setList(updated)
  }

  const addListItem = (list: string[], setList: (v: string[]) => void) => {
    setList([...list, ''])
  }

  const removeListItem = (
    list: string[],
    setList: (v: string[]) => void,
    index: number
  ) => {
    if (list.length <= 1) return
    setList(list.filter((_, i) => i !== index))
  }

  const handleSaveDraft = async () => {
    if (!id) return
    setSaving(true)
    try {
      const updated = await evaluationsApi.updateEvaluation(parseInt(id), {
        ...buildScorePayload(),
        strengths: strengths.filter((s) => s.trim()),
        improvements: improvements.filter((s) => s.trim()),
        summary,
        red_flags: redFlags
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        notes,
      })
      setEvaluation((prev) => (prev ? { ...prev, ...updated } : prev))
    } catch (err) {
      console.error('Failed to save:', err)
      alert(err instanceof Error ? err.message : 'Failed to save evaluation.')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!id) return
    if (!window.confirm('Approve and send this evaluation to the client?'))
      return
    setSaving(true)
    try {
      // Save latest content first
      await evaluationsApi.updateEvaluation(parseInt(id), {
        ...buildScorePayload(),
        strengths: strengths.filter((s) => s.trim()),
        improvements: improvements.filter((s) => s.trim()),
        summary,
        red_flags: redFlags
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        notes,
      })
      const approved = await evaluationsApi.approveEvaluation(parseInt(id))
      setEvaluation((prev) =>
        prev ? { ...prev, status: approved.status ?? 'delivered' } : prev
      )
    } catch (err) {
      console.error('Failed to approve:', err)
      alert(
        err instanceof Error ? err.message : 'Failed to approve evaluation.'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateAiSummary = async () => {
    if (!id) return
    setGeneratingAi(true)
    try {
      const result = await evaluationsApi.generateAiSummary(parseInt(id))
      // AiSummaryResponse only returns summary + ai_confidence_score
      if (result.summary) setSummary(result.summary)
    } catch (err) {
      console.error('Failed to generate AI summary:', err)
      alert(
        err instanceof Error
          ? err.message
          : 'Failed to generate AI summary. Please try again.'
      )
    } finally {
      setGeneratingAi(false)
    }
  }

  if (loading) {
    return <div className="loading-screen">Loading evaluation...</div>
  }

  if (error || !evaluation) {
    return (
      <div className="empty-state">
        <FileText size={48} />
        <h3>{error || 'Evaluation not found'}</h3>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/interviewer/evaluations')}
        >
          Back to Evaluations
        </button>
      </div>
    )
  }

  // Read-only once delivered
  const isReadOnly = evaluation.status === 'delivered'

  const statusBadge = () => {
    switch (evaluation.status) {
      case 'draft':
        return (
          <span className="badge badge-warning">
            <Clock size={10} /> Draft
          </span>
        )
      case 'interviewer_review':
        return (
          <span className="badge badge-primary">
            <FileText size={10} /> Under Review
          </span>
        )
      case 'delivered':
        return (
          <span className="badge badge-success">
            <CheckCircle size={10} /> Delivered
          </span>
        )
      default:
        return <span className="badge">{evaluation.status}</span>
    }
  }

  const sourceLabel =
    evaluation.session_title ||
    (evaluation.session_id
      ? `Session #${evaluation.session_id}`
      : evaluation.booking_id
        ? `Booking #${evaluation.booking_id}`
        : 'Interview')

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button
          className="btn btn-secondary"
          style={{ padding: '8px 16px', fontSize: 13, marginBottom: 16 }}
          onClick={() => navigate('/interviewer/evaluations')}
        >
          <ArrowLeft size={16} />
          Back to Evaluations
        </button>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h1 className="page-title">Evaluation Report</h1>
            <p className="page-subtitle">
              {evaluation.client_name || 'Client'} — {sourceLabel}
            </p>
          </div>
          {statusBadge()}
        </div>
      </div>

      {/* Competency Scores */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Competency Scores</h3>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--primary-navy)',
            }}
          >
            Overall: {calculateOverall()} / 5
          </div>
        </div>
        {competencies.map((comp) => (
          <div
            key={comp.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '16px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                {comp.name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {comp.description} (weight: {comp.weight}%)
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() =>
                    !isReadOnly && handleScoreChange(comp.id, star)
                  }
                  disabled={isReadOnly}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isReadOnly ? 'default' : 'pointer',
                    padding: 2,
                    color:
                      star <= (scores[comp.id] || 0) ? '#d4af37' : '#cbd5e1',
                  }}
                >
                  <Star
                    size={24}
                    fill={
                      star <= (scores[comp.id] || 0) ? 'currentColor' : 'none'
                    }
                  />
                </button>
              ))}
              <span
                style={{
                  marginLeft: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  minWidth: 24,
                  textAlign: 'center',
                }}
              >
                {scores[comp.id] || '-'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Strengths & Improvements */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
          marginBottom: 24,
        }}
      >
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Strengths
          </h3>
          {strengths.map((s, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 8,
                alignItems: 'center',
              }}
            >
              <input
                type="text"
                className="form-input"
                value={s}
                onChange={(e) =>
                  handleListChange(strengths, setStrengths, i, e.target.value)
                }
                placeholder="Enter a strength..."
                disabled={isReadOnly}
              />
              {!isReadOnly && strengths.length > 1 && (
                <button
                  onClick={() => removeListItem(strengths, setStrengths, i)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--danger)',
                    padding: 4,
                    flexShrink: 0,
                  }}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {!isReadOnly && (
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: 13 }}
              onClick={() => addListItem(strengths, setStrengths)}
            >
              + Add
            </button>
          )}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Areas for Improvement
          </h3>
          {improvements.map((s, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 8,
                alignItems: 'center',
              }}
            >
              <input
                type="text"
                className="form-input"
                value={s}
                onChange={(e) =>
                  handleListChange(
                    improvements,
                    setImprovements,
                    i,
                    e.target.value
                  )
                }
                placeholder="Enter an area for improvement..."
                disabled={isReadOnly}
              />
              {!isReadOnly && improvements.length > 1 && (
                <button
                  onClick={() =>
                    removeListItem(improvements, setImprovements, i)
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--danger)',
                    padding: 4,
                    flexShrink: 0,
                  }}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {!isReadOnly && (
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: 13 }}
              onClick={() => addListItem(improvements, setImprovements)}
            >
              + Add
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Summary</h3>
          {!isReadOnly && (
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
              onClick={handleGenerateAiSummary}
              disabled={generatingAi}
            >
              <Sparkles size={14} />
              {generatingAi ? 'Generating...' : 'Generate AI Summary'}
            </button>
          )}
        </div>
        <textarea
          className="form-input"
          placeholder="Write a comprehensive summary of the interview..."
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={5}
          disabled={isReadOnly}
        />
      </div>

      {/* Red Flags (Internal Only) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={18} style={{ color: 'var(--danger)' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>
            Red Flags (Internal Only)
          </h3>
        </div>
        <p
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          This section is NOT visible to the client. One flag per line.
        </p>
        <textarea
          className="form-input"
          placeholder="Any red flags or concerns (one per line)..."
          value={redFlags}
          onChange={(e) => setRedFlags(e.target.value)}
          rows={3}
          disabled={isReadOnly}
        />
      </div>

      {/* Internal Notes */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
          Internal Notes
        </h3>
        <textarea
          className="form-input"
          placeholder="Additional internal notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={isReadOnly}
        />
      </div>

      {/* Action Buttons */}
      {!isReadOnly && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            paddingBottom: 40,
          }}
        >
          <button
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            className="btn btn-success"
            onClick={handleApprove}
            disabled={saving}
          >
            <Send size={18} />
            Approve & Send to Client
          </button>
        </div>
      )}
    </div>
  )
}
